import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products, productCategories } from '@/mocks/products';

export default function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProducts = useMemo(() => {
    let result = products;
    if (activeCategory !== 'all') {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const catLabel = (cat: (typeof productCategories)[number]) => {
    return cat.name;
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20luxury%20shopping%20background%20with%20warm%20terracotta%20and%20sage%20green%20gradients%2C%20soft%20bokeh%20lights%2C%20premium%20fashion%20products%20silhouettes%2C%20editorial%20e-commerce%20aesthetic%2C%20muted%20earth%20tones%2C%20high%20resolution&width=1920&height=600&seq=products-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/30 to-foreground-950/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight drop-shadow-sm">
              Product Catalog
            </h1>
            <p className="mt-4 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
              Browse curated products from trusted sellers. Click any item to view details and purchase on the original platform.
            </p>

            {/* Search */}
            <div className="mt-8 max-w-xl mx-auto">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 w-5 h-5 flex items-center justify-center" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 md:py-4 bg-white/90 backdrop-blur-sm border border-white/20 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="sticky top-16 md:top-18 z-40 bg-white/95 backdrop-blur-md border-b border-background-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              All Products
            </button>
            {productCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {catLabel(cat)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-10 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Results count */}
          <div className="mb-6 md:mb-8 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
              {activeCategory !== 'all' && (
                <span className="ml-1">
                  in{' '}
                  <span className="font-medium text-foreground-700">
                    {productCategories.find((c) => c.id === activeCategory)?.name}
                  </span>
                </span>
              )}
              {searchQuery && (
                <span className="ml-1">
                  for "<span className="font-medium text-foreground-700">{searchQuery}</span>"
                </span>
              )}
            </p>
            {(activeCategory !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setActiveCategory('all');
                  setSearchQuery('');
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
                Clear filters
              </button>
            )}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-16 md:py-24">
              <i className="ri-shopping-bag-3-line w-12 h-12 flex items-center justify-center mx-auto text-foreground-300 mb-4 text-3xl" />
              <p className="text-lg font-medium text-foreground-700">No products found</p>
              <p className="text-sm text-foreground-500 mt-1">Try adjusting your search or filters</p>
              <button
                onClick={() => {
                  setActiveCategory('all');
                  setSearchQuery('');
                }}
                className="mt-4 px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors"
              >
                View all products
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {filteredProducts.map((product) => (
                <a
                  key={product.id}
                  href={product.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group block bg-white border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200/70 hover:shadow-md transition-all"
                >
                  <div className="aspect-square overflow-hidden bg-background-100 relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-white/90 backdrop-blur-sm text-foreground-700 rounded-md border border-background-200/50">
                        {productCategories.find((c) => c.id === product.category)?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-baseline gap-2">
                        <span className="text-base font-semibold text-primary-600">{product.priceYuan}</span>
                        <span className="text-sm text-foreground-400">{product.priceDollar}</span>
                      </div>
                      <span className="flex items-center gap-1 text-sm font-medium text-primary-600 group-hover:translate-x-0.5 transition-transform">
                        Buy
                        <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-foreground-400">ID: {product.id}</p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}